import { transformDocumentFieldFilter } from '@dealstack/frontend/app/pages/Project/MIP/utils/transformDocumentFieldFilter';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import {
	BaseQuickFiltersDto,
	BaseSortByDto,
	InvestmentProgrammeDocumentsFieldFilterQueryDto,
	InvestmentProgrammeDocumentsRes,
	PaginationDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { buildInvestmentProgrammeDocumentsListKeyPrefix } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/documentQueryKeys';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { InvestmentProgrammeDocumentsDataOperations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils.types';

export const useInvestmentProgrammeDocuments = (options: {
	dealId: string;
	programmeId: string;
	dataOperations: InvestmentProgrammeDocumentsDataOperations;
	eligibleDocumentFilterMode?:
		| 'reissue'
		| 'revoke'
		| 'sign'
		| 'assign-counterparty'
		| 'claim-on-behalf'
		| 'invite-authorised-signatory'
		| 'remind'
		| 'apply-date'
		| null;
	currentUserId?: string;
}) => {
	const {
		dealId,
		programmeId,
		dataOperations,
		eligibleDocumentFilterMode,
		currentUserId,
		...queryOptions
	} = options;

	const endpoint = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET,
		{ dealId, programmeId }
	);

	const transformedFieldFilter = transformDocumentFieldFilter(dataOperations.filter);

	const quickFilters: BaseQuickFiltersDto = {
		...dataOperations.quickFilters,
		...(eligibleDocumentFilterMode === 'reissue' ? { reissueable: true } : {}),
		...(eligibleDocumentFilterMode === 'revoke' ? { revokeable: true } : {}),
		...(eligibleDocumentFilterMode === 'sign' && currentUserId
			? { signableByCurrentUser: currentUserId }
			: {}),
		...(eligibleDocumentFilterMode === 'assign-counterparty'
			? { hasOpenCounterpartyRoles: true }
			: {}),
		// This filter still applies the pre-DC-1094 "investor has signed" rule, so it is
		// narrower than what the backend now permits (file final is enough). Pending migration.
		...(eligibleDocumentFilterMode === 'invite-authorised-signatory'
			? { invitableAuthorisedSignatory: true }
			: {}),
		...(eligibleDocumentFilterMode === 'claim-on-behalf'
			? { hasClaimableInvestorSigners: true }
			: {}),
		...(eligibleDocumentFilterMode === 'remind' ? { remindable: true } : {}),
		...(eligibleDocumentFilterMode === 'apply-date' ? { awaitingPostDating: true } : {}),
	};

	const queryString: {
		pagination?: PaginationDto;
		sortBy?: BaseSortByDto;
		fieldFilter?: InvestmentProgrammeDocumentsFieldFilterQueryDto;
		quickFilters?: BaseQuickFiltersDto;
	} = {
		pagination: dataOperations.pagination,
		sortBy: dataOperations.sortBy?.[0] as BaseSortByDto,
		fieldFilter: transformedFieldFilter,
		quickFilters,
	};

	return useQuery({
		enabled: !!dealId && !!programmeId,
		queryKey: [
			...buildInvestmentProgrammeDocumentsListKeyPrefix({ dealId, programmeId }),
			queryString,
		],
		queryFn: async () => {
			const response = await makeRequest<ApiV1.Response<InvestmentProgrammeDocumentsRes>>({
				endpoint,
				method: HTTP_METHOD.GET,
				queryString,
			});
			return response;
		},
		placeholderData: keepPreviousData,
		...queryOptions,
	});
};
